// @LTG-ID: data/journals/does-growth-increase-business-value
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: /app/docs/data-model/journal_content_v1.md, /app/docs/09_CHANGELOG.md#CHG-0107
// @CHANGED: 2026-07-06 (CHG-0107 · Journals content port from batch MD)
// @OWNER: web
//
// Article 17 of 20. Source: batch MD at /app/_ltg_analysis/content_batches/.
// Byline: LTG Team (per CHG-0107 orchestrator Decision 2 / batch preamble directive).

export const BRIEF = {
    SLUG: "does-growth-increase-business-value",
    FRONTMATTER: {
        number: "54",
        section: "Value Drivers",
        sectionHref: "/journals#value-drivers",
        sectionAnchor: "value-drivers",
        title: "Does Growth Increase Business Value? What Buyers Actually Pay For",
        deck: "Yes, growth increases business value, but buyers pay for demonstrated, profitable, sustainable growth rather than projections.",
        author: "LTG Team",
        authorRole: "",
        authorInitials: "LT",
        publishedISO: "2026-04-04",
        publishedHuman: "April 4, 2026",
        lastReviewed: "2026-08-27",
        readMinutes: 3,
        wordCount: 405,
        timeRequired: "PT3M",
        keywords: "does growth increase business value, revenue growth business valuation, growth trend selling a business, declining business value",
        articleSection: "Value Drivers",
        articleBodyPreview: "Yes, growth increases business value, but buyers pay for demonstrated, profitable, sustainable growth rather than projections. A consistent multi year upward trend supports pricing at the top of a business's valuation range. Unprofitable growth, one time spikes, and hockey stick forecasts without evidence carry little weight with buyers. Every seller's projections show growth. Buyers know this, which is why projections are the least persuasive page in any offering document. What moves price is t",
        complianceNote: "This article is educational and does not constitute financial advice.",
        needsDisclaimerFooter: false,
        internalLinks: [
        "how-much-is-my-business-worth",
        "what-is-a-valuation-multiple",
        "business-value-drivers"
    ],
        ctaId: "journal-article-primary",
        ctaLabel: "Get Your Valuation",
        pageTitle: "Does Growth Increase Business Value? What Buyers Pay · LTG",
        canonical: "https://letstalkgrowth.com/journals/does-growth-increase-business-value",
        ogImage: "/journals/og/does-growth-increase-business-value.jpg",
    },
    BODY_MARKDOWN: `Yes, growth increases business value, but buyers pay for demonstrated, profitable, sustainable growth rather than projections. A consistent multi-year upward trend supports pricing at the top of a business's valuation range. Unprofitable growth, one-time spikes, and hockey-stick forecasts without evidence carry little weight with buyers.

Every seller's projections show growth. Buyers know this, which is why projections are the least persuasive page in any offering document. What moves price is the growth that already happened.

## The trend is the story

Buyers evaluate trailing performance, typically three years, because the trend answers a question no single year can: which direction is this business heading, and why? A steady climb tells a story of momentum a new owner can inherit. A flat line tells a story of stability, worth something, but not a premium. A decline demands an explanation, and even good explanations, an owner winding down deliberately, a one-time customer loss already replaced, cost negotiating leverage because they must be believed rather than observed.

## Not all growth is equal

Buyers separate growth into kinds, and pay differently for each:

- **Profitable growth** is the premium kind: revenue rising with margins intact or improving.
- **Bought growth**, revenue purchased through unsustainable discounting or unprofitable customers, gets unwound in a buyer's analysis and can actually hurt, since it muddies the earnings picture.
- **Spike growth**, one large project or an unusual year, gets normalized away. Buyers pay for the repeatable level, not the peak.
- **Capacity-constrained growth** is an interesting special case: a business turning away work it cannot staff or equip is showing buyers documented demand, which can support value even without the revenue, if the evidence is real.

## The seller's timing dilemma

The uncomfortable corollary: the best time to sell is while the trend is still rising, which is precisely when owners least want to. Owners who ride growth to its plateau, then decide to exit, sell a flat business. Owners who exit into strength sell momentum, and momentum is what commands the top of the range. This is a personal decision as much as a financial one, but sellers should at least make it knowingly.

One more practical note: keep performing during the sale process itself. Deals reprice when trailing twelve-month numbers sag mid-process, and the distraction of a sale is a common cause. Sellers who assign the deal to advisors and keep running the business protect their own price.`,
    FAQ: [
        { question: "How many years of growth do buyers want to see?", answer: "Three years is the standard evaluation window. Consistent growth across that window, with maintained margins, supports premium pricing far more than a single strong recent year." },
        { question: "Is a declining business unsellable?", answer: "No, but it sells differently: lower in the range, with more buyer skepticism and often more seller financing or earnout structure. A credible, documented explanation for the decline, and early signs of stabilization, improve the outcome." },
        { question: "Do buyers pay for projections?", answer: "Rarely. Projections frame the opportunity, but pricing anchors to demonstrated results. The exception is when projections are backed by contracts already signed, which is really just recurring revenue wearing a different label." }
    ],
    SOURCES: [

    ],
    END_CTA: {
        label: "READY TO KNOW YOUR NUMBER?",
        headlineLead: "What is your business ",
        headlineEm: "actually",
        headlineTrail: " worth?",
        body: "Indicative valuation signed by Sara. $995 flat.",
        secondaryHref: "/journals",
        secondaryLabel: "More from the Journal →",
        microLine: "· 7-DAY DELIVERY · KGOB METHODOLOGY, NC CPA #30420 ·",
    },
    READ_NEXT: [
        {
            tag: "Valuation",
            title: "How Much Is My Business Worth? The Owner's Complete Guide to Business Valuation",
            meta: "5 min · Brief 38",
            to: "/journals/how-much-is-my-business-worth",
        },
        {
            tag: "Valuation",
            title: "What Is a Valuation Multiple and Why Does Everyone Talk About Them?",
            meta: "3 min · Brief 42",
            to: "/journals/what-is-a-valuation-multiple",
        },
        {
            tag: "Value Drivers",
            title: "The Value Drivers That Determine What Buyers Will Pay for Your Business",
            meta: "4 min · Brief 48",
            to: "/journals/business-value-drivers",
        },
    ],
};

export default BRIEF;
